import { jsx } from 'react/jsx-runtime';
import { d as EGDSLayoutTags } from '../types/index-chunk.esm2.js';
import * as React from 'react';
import { c as classNames } from '../theme-provider/index-chunk.esm.js';
import styles from '../styles/uitk-egds-layout-grid.css';
import { M as MakeEgdsSizingLength } from '../egds-sizing-length/index-chunk.esm.js';

// --
// Grid types
// --
const EGDSLayoutGridColumnRows = [1, 2, 3, 4, 6, 8, 12];
const EgdsSizingLengths = [
    '0x',
    '0x__half',
    '0x__quarter',
    '1x',
    '1x__half',
    '2x',
    '3x',
    '3x__half',
    '4x',
    '4x__half',
    '5x',
    '6x',
    '7x',
    '8x',
    '9x',
    '10x',
    '11x',
    '12x',
    '13x',
    '14x',
    '15x',
    '16x',
    '17x',
    '18x',
    '24x',
    '30x',
    '37x',
    '38x',
    '40x',
    '48x',
    '70x',
    '72x',
    '76x',
    '88x',
    '96x',
    '104x',
    '112x',
    '120x',
    '180x',
    '312x',
    '500x',
];
const EGDSLayoutGridAligns = ['start', 'end', 'center', 'stretch'];
const EGDSLayoutGridPlaces = [
    'start',
    'end',
    'center',
    'stretch',
    'space-around',
    'space-between',
    'space-evenly',
];
const EGDSLayoutGridAutoFlows = ['row', 'column', 'dense', 'row-dense', 'column-dense'];
/**
 * Keyword block-size (height) sizes
 *
 * @type 'full_size'
 */
const EGDSLayoutGridBlockSizes = ['full_size'];
// --
// Grid item types
// --
const EGDSLayoutGridSpans = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const EGDSLayoutGridItemPlaceSelfs = ['start', 'end', 'center', 'stretch'];

/**
 * Check string is a valid EgdsSizingLength value
 *
 * @param size {string} - size
 * @returns EgdsSizingLengths.includes(size as EgdsSizingLength)
 */
const IsEgdsSize = (size) => EgdsSizingLengths.includes(size);
/**
 * Check string is a valid FlexibleEgdsSizingLength value
 *
 * @param length {string} - length
 * @returns FlexibleEgdsSizingLength.includes(size as EgdsSizingLength)
 */
const IsFlexibleEgdsSize = (length) => length.endsWith('px');
/**
 * Check for deprecated "auto_fit" or "auto_fill" keys
 *
 * @param obj {UitkLayoutConditionalGridTrack} - object
 * @returns boolean
 */
const isAutoRepeatGrid = (obj) => Object.prototype.hasOwnProperty.call(obj, 'auto_fill') || Object.prototype.hasOwnProperty.call(obj, 'auto_fit');
/**
 * Check for "small" key
 *
 * This is used when checking values that can be applied per breakpoint, as "small" queries are
 * treated as regular values (ie: not encased in a media query when used in the CSS)
 *
 * @param key {string} - key
 * @returns boolean
 */
const isSmallKey = (key) => key === 'small';
/**
 * Outputs a css custom property for a valid EgdsSizingLength value
 *
 * @param value {EgdsSizingLength} - value
 * @returns `var(--uitk-layoutgrid-egds-size__${value})`
 */
const EgdsSizeCustomProp = (value) => `var(--uitk-layoutgrid-egds-size__${value})`;
/**
 * Returns a track item, checking if it is an EGDS size that needs to be processed
 *
 * @param item {any} - track item to return
 */
const MaketrackItem = (item) => {
    let trackItem;
    if (IsEgdsSize(item)) {
        trackItem = EgdsSizeCustomProp(item);
    }
    else if (IsFlexibleEgdsSize(item)) {
        trackItem = MakeEgdsSizingLength(item);
    }
    else {
        trackItem = item;
    }
    return trackItem;
};
/**
 * Outputs a css fit-content() function
 *
 * @param length {EgdsSizingLength | FlexibleEgdsSizingLength} - length
 * @returns {`fit-content(${typeof fitContentValue})`} `fit-content(length)`
 */
const FitContent = (length) => {
    const fitContentValue = MaketrackItem(length);
    const fitcontentValue = `fit-content(${fitContentValue})`;
    return fitcontentValue;
};
/**
 * Outputs a css minmax() function
 *
 * @param min {UitkLayoutGridLength} - min value
 * @param max {UitkLayoutGridLength} - max value
 * @returns {`minmax(${typeof minValue}, ${typeof maxValue})`} `minmax(min, max)`
 */
const MinMax = (min, max) => {
    const minValue = MaketrackItem(min);
    const maxValue = MaketrackItem(max);
    const minmaxValue = `minmax(${minValue}, ${maxValue})`;
    return minmaxValue;
};
/**
 * Outputs a css repeat() function
 *
 * @param repeatType {number | 'auto-fit' | 'auto-fill'} - repeat type
 * @param track {UitkLayoutGridLength | ReturnType<typeof MinMax> | ReturnType<typeof FitContent>} - track to repeat
 * @returns {`repeat(${typeof repeatType}, ${typeof track})`} `repeat(repeatType, track)`
 */
const Repeat = (repeatType, track) => {
    const trackValue = MaketrackItem(track);
    const repeatValue = `repeat(${repeatType}, ${trackValue})`;
    return repeatValue;
};

const getClassNameList$1 = classNames.bind(styles);
const baseClass = 'uitk-layout-grid';
/**
 * Set a valid grid track value
 *
 * @param value {any}
 * @returns string
 */
const trackList = (value) => {
    const track = [];
    const isArray = Array.isArray(value);
    const isNumber = typeof value === 'number';
    if (isArray) {
        value.forEach((item) => track.push(MaketrackItem(item)));
    }
    else if (isNumber) {
        track.push(`repeat(${value}, minmax(0, 1fr))`); // Include minmax() to ensure unbroken content doesn't force grid out
    }
    else {
        track.push(MaketrackItem(value));
    }
    return track.join(' ');
};
/**
 * Generate a valid CSS custom prop / value object for grid areas
 *
 * @param areas {UitkLayoutGridArea | UitkLayoutConditionalGridArea}
 * @returns Object as React.CSSProperties
 */
const areasValue = (areas) => {
    const isObject = !Array.isArray(areas) && typeof areas === 'object';
    const obj = {};
    const areaRows = (value) => value.map((area) => `"${area}"`).join(' ');
    if (isObject) {
        Object.entries(areas).map(([key, val]) => Object.assign(obj, {
            [`--uitk-layoutgrid-areas${isSmallKey(key) ? '' : `-${key}`}`]: areaRows(val),
        }));
    }
    else {
        Object.assign(obj, { [`--uitk-layoutgrid-areas`]: areaRows(areas) });
    }
    return obj;
};
/**
 * Generate a valid CSS custom prop / value object for grid gap
 *
 * @param value {UitkLayoutSpace | UitkLayoutGridSpaceInterface}
 * @returns Object as React.CSSProperties
 */
const spaceValue = (value) => {
    const isString = typeof value === 'string';
    const colValue = isString ? value : value.column;
    const rowValue = isString ? value : value.row;
    return {
        ...(colValue && { [`--uitk-layoutgrid-column-gap`]: `var(--uitk-layoutgrid-space-${colValue})` }),
        ...(rowValue && { [`--uitk-layoutgrid-row-gap`]: `var(--uitk-layoutgrid-space-${rowValue})` }),
    };
};
/**
 * Set a valid value for the deprecated UitkLayoutGridAutoRepeat interface
 *
 * @param repeatType {string} Type of repeat
 * @param minValue {any} min value for the minmax() part of the repeat() function
 * @param suffix {string} suffix for the custom property name
 * @returns object as React.CSSProperties
 */
const autoRepeatGridValue = (repeatType, minValue, suffix) => {
    const newRepeatType = repeatType.replace('_', '-');
    return {
        [`--uitk-layoutgrid-${suffix}`]: `repeat(${newRepeatType}, minmax(var(--uitk-layoutgrid-egds-size__${minValue}), 1fr))`,
    };
};
/**
 * Generate a valid CSS custom prop / value object for:
 *
 * - columns
 * - rows
 * - autoColumns
 * - autoRows
 *
 * @param value {UitkLayoutSpace | UitkLayoutGridSpaceInterface}
 * @returns Object as React.CSSProperties
 */
const gridTrackValue = (value, suffix) => {
    const isObject = !Array.isArray(value) && typeof value === 'object';
    const obj = {};
    if (isObject) {
        Object.entries(value).map(([key, val]) => Object.assign(obj, isAutoRepeatGrid(value)
            ? autoRepeatGridValue(key, val, suffix)
            : { [`--uitk-layoutgrid-${suffix}${isSmallKey(key) ? '' : `-${key}`}`]: trackList(val) }));
    }
    else {
        Object.assign(obj, { [`--uitk-layoutgrid-${suffix}`]: trackList(value) });
    }
    return obj;
};
/**
 * Generate a valid CSS custom prop for blockSize
 *
 * @param value {UitkLayoutGridBlockSize}
 * @param suffix {string}
 * @returns Object as React.CSSProperties
 */
const blockSizeValue = (value, suffix) => {
    const obj = {};
    let newValue;
    if (value !== 'full_size') {
        // If value is a viewport block (vb) length,
        // convert to viewport height (vh) for the time being,
        // until browser support matches our requirements
        // https://caniuse.com/mdn-css_types_length_vb
        newValue = value.replace('vb', 'vh');
        Object.assign(obj, { [`--uitk-layoutgrid-${suffix}`]: newValue });
    }
    return obj;
};
/**
 * Prevent invalid CSS Custom Properties from being applied
 *
 * @param value {UitkLayoutGridArea | UitkLayoutConditionalGridArea | UitkLayoutGridTrack | UitkLayoutConditionalGridTrack}
 * @param suffix {string}
 * @param baseCSSClass {string}
 * @returns getClassNameList()
 */
const conditionalGridTrackClassName = (value, suffix, baseCSSClass = baseClass) => {
    const isObject = !Array.isArray(value) && typeof value === 'object';
    const isautoGrid = isObject &&
        (Object.prototype.hasOwnProperty.call(value, 'auto_fill') ||
            Object.prototype.hasOwnProperty.call(value, 'auto_fit'));
    return getClassNameList$1(isObject
        ? Object.entries(value).map(([key]) => `${baseCSSClass}-has-${suffix}${isSmallKey(key) ? '' : `-by-${key}`}`)
        : `${baseCSSClass}-has-${suffix}`, isautoGrid && `${baseCSSClass}-has-${suffix}-using-auto-grid`);
};
const conditionalBlockSizeClassName = (value, suffix, baseCSSClass = baseClass) => (value === 'full_size' ? `${baseCSSClass}-${suffix}-${value}` : `${baseCSSClass}-${suffix}-explicit_size`);
const wrapperClassNames = ({ areas, alignContent, alignItems, autoFlow, autoColumns, autoRows, blockSize, className, columns, display, justifyContent, justifyItems, minBlockSize, rows, space, }) => getClassNameList$1(baseClass, alignContent && `${baseClass}-align-content-${alignContent}`, alignItems && `${baseClass}-align-items-${alignItems}`, areas && conditionalGridTrackClassName(areas, 'areas'), autoColumns && `${baseClass}-has-auto-columns`, autoRows && `${baseClass}-has-auto-rows`, columns && conditionalGridTrackClassName(columns, 'columns'), rows && conditionalGridTrackClassName(rows, 'rows'), space && `${baseClass}-has-space`, autoFlow && `${baseClass}-auto-flow-${autoFlow}`, blockSize && conditionalBlockSizeClassName(blockSize, 'block-size'), display && `${baseClass}-display-${display}`, justifyContent && `${baseClass}-justify-content-${justifyContent}`, justifyItems && `${baseClass}-justify-items-${justifyItems}`, minBlockSize && `${baseClass}-min-block-size-${minBlockSize}`, className);
const EGDSLayoutGrid = (props) => {
    const { alignContent, alignItems, areas, autoColumns = [MinMax('0x', '1fr')], autoFlow, autoRows, blockSize, children, display = 'grid', space, columns, className, justifyContent, justifyItems, minBlockSize, rows, tag, dataTestId, style, ...attributes } = props;
    const wrapperClassNameProps = {
        alignContent,
        alignItems,
        autoFlow,
        autoColumns,
        autoRows,
        areas,
        blockSize,
        className,
        columns,
        display,
        justifyContent,
        justifyItems,
        minBlockSize,
        rows,
        space,
    };
    const gridCustomProps = {
        ...(areas && areasValue(areas)),
        ...(autoColumns && gridTrackValue(autoColumns, 'auto-columns')),
        ...(autoRows && gridTrackValue(autoRows, 'auto-rows')),
        ...(columns && gridTrackValue(columns, 'columns')),
        ...(rows && gridTrackValue(rows, 'rows')),
        ...(space && spaceValue(space)),
        ...(blockSize && blockSizeValue(blockSize, 'block-size')),
        ...(style && style),
    };
    // Renders the custom Tag
    const Tag = ((tag && EGDSLayoutTags.get(tag)) || EGDSLayoutTags.get('div'));
    return children ? (jsx(Tag, { className: wrapperClassNames({ ...wrapperClassNameProps }), style: gridCustomProps, ...attributes, ...(dataTestId && { 'data-test-id': dataTestId }), children: children })) : null;
};
EGDSLayoutGrid.displayName = 'EGDSLayoutGrid';
const withEGDSLayoutGrid = (WrappedComponent) => {
    const WithEGDSLayoutGrid = (props) => {
        const { alignContent, alignItems, autoColumns, autoFlow, autoRows, areas, blockSize, className, children, columns, display, justifyContent, justifyItems, minBlockSize, rows, space, style, } = props;
        const wrapperClassNameProps = {
            alignContent,
            alignItems,
            autoFlow,
            autoColumns,
            autoRows,
            areas,
            blockSize,
            className,
            columns,
            display,
            justifyContent,
            justifyItems,
            minBlockSize,
            rows,
            space,
        };
        const gridCustomProps = {
            ...(areas && areasValue(areas)),
            ...(autoColumns && gridTrackValue(autoColumns, 'auto-columns')),
            ...(autoRows && gridTrackValue(autoRows, 'auto-rows')),
            ...(columns && gridTrackValue(columns, 'columns')),
            ...(rows && gridTrackValue(rows, 'rows')),
            ...(space && spaceValue(space)),
            ...(blockSize && blockSizeValue(blockSize, 'block-size')),
            ...(style && style),
        };
        return (jsx(WrappedComponent, { className: wrapperClassNames({ ...wrapperClassNameProps }), style: gridCustomProps, children: children || null }));
    };
    WithEGDSLayoutGrid.displayName = `withUitkLayoutGrid(${WrappedComponent.displayName})`;
    return WithEGDSLayoutGrid;
};

const getClassNameList = classNames.bind(styles);
const EGDSLayoutGridItem = (props) => {
    const { alignSelf, area, children, className, colEnd, colSpan, colStart, dataTestId, justifySelf, rowSpan, rowStart, rowEnd, style, } = props;
    const childrenArray = React.Children.toArray(children);
    if (!childrenArray.length) {
        return null;
    }
    const firstChild = childrenArray[0];
    const firstChildClassName = firstChild.props.className;
    const firstChildStyle = firstChild.props.style;
    const baseClass = 'uitk-layout-grid-item';
    const itemCustomPropValue = (suffix, value, isSpan) => {
        const isEnd = value === 'end';
        const isAll = value === 'all';
        let returnValue = {};
        if (isEnd) {
            returnValue = {
                [`--uitk-layoutgrid-${suffix}`]: `-1`,
            };
        }
        else if (isAll) {
            returnValue = {
                [`--uitk-layoutgrid-${suffix}`]: `1`,
                [`--uitk-layoutgrid-${suffix.replace('start', 'end')}`]: `-1`,
            };
        }
        else {
            returnValue = {
                [`--uitk-layoutgrid-${suffix}`]: isSpan ? `span ${value}` : `${value}`,
            };
        }
        return returnValue;
    };
    const MakeCustomProp = (suffix, value, isSpan) => {
        const isObject = !Array.isArray(value) && typeof value === 'object';
        const obj = {};
        if (isObject) {
            Object.entries(value).map(([key, val]) => Object.assign(obj, itemCustomPropValue(`${suffix}${isSmallKey(key) ? '' : `-${key}`}`, val, isSpan)));
        }
        else {
            Object.assign(obj, itemCustomPropValue(suffix, value, isSpan));
        }
        return obj;
    };
    /**
     * Prevent invalid CSS Custom Properties from being applied
     *
     * @param value {UitkLayoutGridItemSpanValues | UitkLayoutConditionalGridSpan | UitkLayoutGridItemStartEndValues | UitkLayoutConditionalGridStartEnd}
     * @param suffix {string}
     * @param baseCSSClass {string}
     * @returns getClassNameList()
     */
    const conditionalGridItemClassName = (value, suffix, baseCSSClass = baseClass) => {
        const isObject = !Array.isArray(value) && typeof value === 'object';
        return getClassNameList(isObject
            ? Object.entries(value).map(([key]) => `${baseCSSClass}-has-${suffix}${isSmallKey(key) ? '' : `-by-${key}`}`)
            : `${baseCSSClass}-has-${suffix}`);
    };
    const wrapperClassNames = getClassNameList(baseClass, firstChildClassName, alignSelf && `${baseClass}-align-self-${alignSelf}`, area &&
        `${baseClass}-has-column-start ${baseClass}-has-column-end ${baseClass}-has-row-start ${baseClass}-has-row-end`, colStart && conditionalGridItemClassName(colStart, 'column-start'), colEnd && conditionalGridItemClassName(colEnd, 'column-end'), colSpan && conditionalGridItemClassName(colSpan, 'column-start'), colSpan && colSpan === 'all' && conditionalGridItemClassName(colSpan, 'column-end'), rowStart && conditionalGridItemClassName(rowStart, 'row-start'), rowEnd && conditionalGridItemClassName(rowEnd, 'row-end'), rowSpan && conditionalGridItemClassName(rowSpan, 'row-start'), rowSpan && rowSpan === 'all' && conditionalGridItemClassName(rowSpan, 'row-end'), justifySelf && `${baseClass}-justify-self-${justifySelf}`, className);
    const gridItemCustomProps = {
        ...(area && {
            [`--uitk-layoutgrid-column-start`]: `${area}-start`,
            [`--uitk-layoutgrid-column-end`]: `${area}-end`,
            [`--uitk-layoutgrid-row-start`]: `${area}-start`,
            [`--uitk-layoutgrid-row-end`]: `${area}-end`,
        }),
        ...(!area && {
            ...(colSpan && MakeCustomProp('column-start', colSpan, true)),
            ...(colStart && MakeCustomProp('column-start', colStart)),
            ...(colEnd && MakeCustomProp('column-end', colEnd)),
            ...(rowSpan && MakeCustomProp('row-start', rowSpan, true)),
            ...(rowStart && MakeCustomProp('row-start', rowStart)),
            ...(rowEnd && MakeCustomProp('row-end', rowEnd)),
        }),
        ...(firstChildStyle && firstChildStyle),
        ...(style && style),
    };
    const decoratedChild = React.cloneElement(firstChild, {
        className: wrapperClassNames,
        style: gridItemCustomProps,
        ...(dataTestId && { 'data-stid': dataTestId }),
    });
    return decoratedChild;
};
EGDSLayoutGridItem.displayName = 'EGDSLayoutGridItem';

export { EGDSLayoutGrid as E, FitContent as F, IsEgdsSize as I, MaketrackItem as M, Repeat as R, EGDSLayoutGridItem as a, IsFlexibleEgdsSize as b, isSmallKey as c, EgdsSizeCustomProp as d, MinMax as e, EGDSLayoutGridColumnRows as f, EgdsSizingLengths as g, EGDSLayoutGridAligns as h, isAutoRepeatGrid as i, EGDSLayoutGridPlaces as j, EGDSLayoutGridAutoFlows as k, EGDSLayoutGridBlockSizes as l, EGDSLayoutGridSpans as m, EGDSLayoutGridItemPlaceSelfs as n, withEGDSLayoutGrid as w };
//# sourceMappingURL=index-chunk.esm.js.map
